import React from 'react';
import { useHistory } from 'react-router-dom';
import { ArrowLeft, Users, MapPin, Phone, Mail, Clock, DollarSign, Building2, ExternalLink, AlarmClock } from 'lucide-react';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}
function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function durationStr(start, end) {
  if (!start || !end) return null;
  const min = Math.round((new Date(end) - new Date(start)) / 60000);
  if (min <= 0) return null;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}
function mapsUrl(addr) {
  if (!addr) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}

/**
 * ShiftHeader — presentational.
 *
 * Props:
 *   shiftData        — { _id, startTime, endTime, staffsRequired, jobId }
 *   customerName     — string
 *   customerLocalId  — string | null  (for CRM link)
 *   customerContact  — { contactPersonName, phone, email, legalAddress, facilityType } | null
 *   serviceType      — string
 *   hourlyPayRate    — number | null
 *   formattedAddress — string | null  (job site address)
 *   location         — string | null  (human-readable location name)
 *   address          — { street, city, state, zipcode } | null
 *   timezone         — string | null
 *   memo             — string | null
 *   filledCount      — number
 *   providerCompanyId — string
 *   onGoBack         — () => void
 *   onGoToJob        — (jobId) => void
 */
const ShiftHeader = ({
  shiftData, customerName, customerLocalId, customerContact,
  serviceType, hourlyPayRate, formattedAddress, location, address, timezone, memo,
  filledCount, providerCompanyId, onGoBack, onGoToJob,
}) => {
  const history = useHistory();

  if (!shiftData) return null;

  const { startTime, endTime, staffsRequired, jobId } = shiftData;
  const isFull    = filledCount >= staffsRequired;
  const isPartial = filledCount > 0 && !isFull;

  const fillBadgeColors = isFull
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : isPartial
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-red-50 text-red-700 border-red-200';
  const fillIcon = isFull ? '●' : isPartial ? '◐' : '○';

  // Display address: prefer formattedAddress, fall back to address fields
  const displayAddr = formattedAddress
    || (address?.street ? [address.street, address.city, address.state, address.zipcode].filter(Boolean).join(', ') : null);

  return (
    <div className="space-y-4">
      {/* Back + breadcrumb row */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onGoBack}
          className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft size={15} />
          Back to Schedule
        </button>
        {jobId && (
          <button
            type="button"
            onClick={() => onGoToJob(jobId)}
            className="text-xs text-muted-foreground transition-colors hover:text-foreground underline-offset-2 hover:underline"
          >
            View Job Allocation →
          </button>
        )}
      </div>

      {/* ── Shift timing banner ── */}
      <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
        {/* Top colour strip */}
        <div className={`px-5 py-3 flex items-center justify-between flex-wrap gap-3 ${
          isFull ? 'bg-emerald-50' : isPartial ? 'bg-amber-50' : 'bg-red-50'
        }`}>
          <div>
            <p className="text-xs font-medium text-muted-foreground">{fmtDate(startTime)}</p>
            <div className="mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                <Clock size={13} className="text-muted-foreground" />
                {fmtTime(startTime)} – {fmtTime(endTime)}
              </span>
              {durationStr(startTime, endTime) && (
                <span className="rounded-full bg-background/70 border border-border px-2 py-0.5 text-xs text-muted-foreground">
                  {durationStr(startTime, endTime)}
                </span>
              )}
              {timezone && (
                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <AlarmClock size={11} />
                  {timezone}
                </span>
              )}
            </div>
          </div>

          {/* Fill badge + CTA */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${fillBadgeColors}`}>
              <Users size={12} />
              {fillIcon} {filledCount}/{staffsRequired} staffed
            </span>
            {!isFull && jobId && (
              <button
                type="button"
                onClick={() => onGoToJob(jobId)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-pink px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-pink/90 active:scale-95"
              >
                Find Officers →
              </button>
            )}
          </div>
        </div>

        {/* ── Body: two-column — customer card left, job site right ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-border">

          {/* LEFT: Customer info */}
          <div className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <Building2 size={13} className="text-muted-foreground flex-shrink-0" />
                  <p className="text-sm font-bold text-foreground truncate">{customerName}</p>
                </div>
                {customerContact?.facilityType && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">{customerContact.facilityType}</p>
                )}
                {serviceType && (
                  <span className="mt-1 inline-block rounded-full bg-brand-blue-dark/8 px-2 py-0.5 text-[11px] font-medium text-brand-blue-dark">
                    {serviceType}
                  </span>
                )}
              </div>

              {/* CRM link */}
              {customerLocalId && (
                <button
                  type="button"
                  onClick={() => history.push(`/provider-company/${providerCompanyId}/crm/${customerLocalId}`)}
                  className="flex-shrink-0 flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:text-foreground hover:border-foreground/30"
                  title="Open customer profile"
                >
                  <ExternalLink size={11} />
                  Profile
                </button>
              )}
            </div>

            {/* Contact info */}
            {(customerContact?.contactPersonName || customerContact?.phone || customerContact?.email) && (
              <div className="space-y-1.5 pt-1 border-t border-border/50">
                {customerContact.contactPersonName && (
                  <p className="text-xs text-muted-foreground">
                    Contact: <span className="font-medium text-foreground">{customerContact.contactPersonName}</span>
                  </p>
                )}
                {customerContact.phone && (
                  <a
                    href={`tel:${customerContact.phone}`}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Phone size={11} />
                    {customerContact.phone}
                  </a>
                )}
                {customerContact.email && (
                  <a
                    href={`mailto:${customerContact.email}`}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Mail size={11} />
                    {customerContact.email}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: Job site info */}
          <div className="p-5 space-y-3">
            {/* Hourly pay rate */}
            {hourlyPayRate && (
              <div className="flex items-center gap-1.5 text-sm">
                <DollarSign size={13} className="text-muted-foreground" />
                <span className="font-semibold text-foreground">${hourlyPayRate.toFixed(2)}</span>
                <span className="text-xs text-muted-foreground">/ hr (bill rate)</span>
              </div>
            )}

            {/* Location name */}
            {location && (
              <div>
                <p className="text-xs font-medium text-foreground">{location}</p>
              </div>
            )}

            {/* Address */}
            {displayAddr && (
              <a
                href={mapsUrl(displayAddr)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors group"
              >
                <MapPin size={12} className="mt-0.5 flex-shrink-0 group-hover:text-brand-pink transition-colors" />
                <span className="group-hover:underline underline-offset-2">{displayAddr}</span>
              </a>
            )}

            {/* Memo */}
            {memo && (
              <div className="rounded-lg bg-muted/40 border border-border/50 px-3 py-2">
                <p className="text-[11px] text-muted-foreground leading-relaxed">{memo}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftHeader;
